import { Link } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Card,
  Grid,
  PageHeader,
  ProgressBar,
  Skeleton,
  Stack,
  Stat,
} from '@mailflow/ui';
import { meQuery } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { overviewQuery } from '../lib/analytics.js';
import { tasksQuery } from '../lib/crm.js';
import { Info } from '../components/Info.js';

interface Onboarding {
  steps: Array<{
    key: string;
    title: string;
    done: boolean;
    why: string;
    href: string;
    detail: string;
  }>;
  done: number;
  total: number;
  counts: { contacts: number; sent: number; deals: number };
}

/**
 * Startseite.
 *
 * Zwei Fälle, ein Aufbau: solange die Einrichtung nicht steht, führt die Liste
 * durch die nächsten Schritte. Danach tritt sie in den Hintergrund und die
 * Kennzahlen übernehmen.
 *
 * Kein Punkt der Liste lässt sich abhaken — jeder wird aus dem tatsächlichen
 * Zustand abgeleitet. Ein Häkchen bedeutet damit immer, dass es die Sache
 * wirklich gibt, und es geht wieder auf, wenn jemand den Absender löscht.
 *
 * Die Kennzahlen bekommen die rohen Werte, nicht bereits formatierte Texte:
 * `Stat` unterscheidet dann selbst zwischen „0 %" und „—". Der Unterschied ist
 * nicht kosmetisch — 0 % heisst, niemand hat geöffnet; „—" heisst, es wurde
 * noch nichts versendet.
 */
export function DashboardPage() {
  const { data: user } = useSuspenseQuery(meQuery);

  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: ({ signal }) => api.get<Onboarding>('/api/onboarding', signal),
    staleTime: 30_000,
  });

  const overview = useQuery(overviewQuery('30d'));
  const tasks = useQuery(tasksQuery({ mine: true, status: 'open' }));

  const offen = onboarding.data?.steps.filter((step) => !step.done) ?? [];
  const eingerichtet = onboarding.data ? onboarding.data.done >= 4 : false;
  const overdue = tasks.data?.items.filter((task) => task.overdue) ?? [];

  return (
    <Stack gap={6}>
      <PageHeader
        title={`Willkommen${user?.name ? `, ${user.name}` : ''}`}
        description={`Angemeldet als ${user?.email ?? '—'}`}
      />

      {overdue.length > 0 && (
        <Alert
          tone="warning"
          action={
            <Link to="/crm/aufgaben" className="btn" data-variant="secondary">
              Ansehen
            </Link>
          }
        >
          {overdue.length === 1
            ? 'Eine Aufgabe ist überfällig.'
            : `${overdue.length} Aufgaben sind überfällig.`}
        </Alert>
      )}

      {/*
       * Die Einrichtungsliste steht oben, solange sie nicht vollständig ist.
       * Wer noch keinen Absender hat, dem nützen Kennzahlen nichts — er kann
       * überhaupt nicht senden.
       */}
      {onboarding.isLoading && (
        <Card>
          <Skeleton lines={4} shape="row" label="Einrichtungsstand wird geladen" />
        </Card>
      )}

      {offen.length > 0 && onboarding.data && (
        <Card
          title="Einrichtung"
          description="Jeder Punkt spiegelt den tatsächlichen Stand. Abhaken lässt sich nichts — ein Häkchen bedeutet, dass es die Sache wirklich gibt."
        >
          <Stack gap={4}>
            <ProgressBar
              label="Fortschritt"
              value={onboarding.data.done / onboarding.data.total}
              caption={`${onboarding.data.done} von ${onboarding.data.total}`}
            />
            <ol className="setup">
              {onboarding.data.steps.map((step) => (
                <li key={step.key} data-done={step.done ? 'true' : undefined}>
                  <div>
                    <strong>{step.title}</strong>
                    <span className="muted text-sm">{step.why}</span>
                    <span className="setup__detail text-sm">{step.detail}</span>
                  </div>
                  {step.done ? (
                    <Badge tone="success">erledigt</Badge>
                  ) : (
                    <Link to={step.href} className="btn" data-variant="secondary">
                      Öffnen
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </Stack>
        </Card>
      )}

      {eingerichtet && (
        <Card
          title="Letzte 30 Tage"
          actions={
            <Link to="/auswertung" className="btn" data-variant="ghost">
              Vollständige Auswertung
            </Link>
          }
        >
          {overview.isLoading ? (
            <Skeleton lines={2} shape="block" label="Kennzahlen werden geladen" />
          ) : overview.data ? (
            <Grid min="sm">
              <Stat
                label="Versendet"
                value={overview.data.totals.sent}
                hint="Nachrichten, die den Server verlassen haben."
              />
              <Stat
                label="Öffnungsrate"
                value={overview.data.rates.openRate === null ? null : overview.data.rates.openRate * 100}
                unit="%"
                info={<Info zu="eindeutigeOeffnung" />}
                hint="Jede Person einmal gezählt, ohne Maschinen."
              />
              <Stat
                label="Klickrate"
                value={overview.data.rates.clickRate === null ? null : overview.data.rates.clickRate * 100}
                unit="%"
                info={<Info zu="klickrate" />}
                hint="Aussagekräftiger als die Öffnungsrate."
              />
              <Stat
                label="Bounce-Rate"
                value={overview.data.rates.bounceRate === null ? null : overview.data.rates.bounceRate * 100}
                unit="%"
                info={<Info zu="bounceRate" />}
                tone={
                  overview.data.rates.bounceRate !== null && overview.data.rates.bounceRate > 0.05
                    ? 'danger'
                    : 'default'
                }
                hint="Über 5 % wird es für die Zustellbarkeit ernst."
              />
            </Grid>
          ) : null}
        </Card>
      )}

      {eingerichtet && onboarding.data && (
        <Grid min="sm">
          <Stat label="Kontakte" value={onboarding.data.counts.contacts} hint="Insgesamt erfasst." />
          <Stat
            label="Offene Deals"
            value={onboarding.data.counts.deals}
            hint="Noch nicht gewonnen oder verloren."
          />
          <Stat
            label="Offene Aufgaben"
            value={tasks.data?.items.length ?? null}
            hint="Dir zugewiesen."
          />
        </Grid>
      )}
    </Stack>
  );
}
